import { type FC, type RefObject, useEffect, useRef } from 'react';

import { ArrowRight } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';

import { CATALOG_CATEGORIES, getCategoryItemCount } from '@entities/catalog';
import { CATALOG_MENU_ID, catalogMenuStore } from '@features/catalog-menu';
import { pluralizeRu, usePresence } from '@shared/lib';
import { tokens } from '@shared/styles/tokens';
import { Picture } from '@shared/ui/Picture';

import styles from './CatalogDropdown.module.css';

export const CatalogDropdown: FC = observer(() => {
  const rootRef = useRef<HTMLElement>(null);
  // Кнопка, открывшая меню: по Escape возвращаем на неё фокус (иначе он улетает в body)
  const triggerRef = useRef<HTMLElement | null>(null);

  // Читаем в рендере observer-компонента: так useEffect видит изменение как обычную зависимость
  const isOpen = catalogMenuStore.isOpen;

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      // target события мыши — всегда Element; instanceof вместо as-каста
      if (!(e.target instanceof Element)) return;
      // Клик по кнопкам-триггерам каталога — пропускаем, catalogMenuStore.toggle() разберётся сам
      if (e.target.closest('[data-catalog-trigger]')) return;
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        catalogMenuStore.close();
      }
    };

    // rAF: ждём следующий кадр чтобы текущий mousedown (открытие) уже ушёл
    const rafId = requestAnimationFrame(() => {
      document.addEventListener('mousedown', handleClickOutside);
    });

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !catalogMenuStore.isOpen) return;
      catalogMenuStore.close();
      // Кнопка «Каталог» из мобильного меню скрыта вместе с ним — тогда фокус на бургер
      const trigger = triggerRef.current;
      const target =
        trigger && trigger.getClientRects().length > 0
          ? trigger
          : document.querySelector<HTMLElement>('[data-menu-focus-fallback]');
      target?.focus();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  // Закрытое меню не рендерится: невидимые ссылки больше не ловят Tab.
  // После закрытия панель живёт ещё menuSec — на анимацию ухода

  const { mounted, closing } = usePresence(isOpen, tokens.motion.menuSec * 1000);
  if (!mounted) return null;
  return <MenuPanel rootRef={rootRef} triggerRef={triggerRef} closing={closing} />;
});

interface MenuPanelProps {
  rootRef: RefObject<HTMLElement | null>;
  triggerRef: RefObject<HTMLElement | null>;
  closing: boolean;
}

const ItemCount: FC<{ slug: string }> = ({ slug }) => {
  const count = getCategoryItemCount(slug);
  if (!count) return null;
  return (
    <span className={styles.cardCount}>
      {count} {pluralizeRu(count, ['позиция', 'позиции', 'позиций'])}
    </span>
  );
};

const AVAILABLE_CATEGORIES = CATALOG_CATEGORIES.filter((category) => !category.isComingSoon);
const UPCOMING_CATEGORIES = CATALOG_CATEGORIES.filter((category) => category.isComingSoon);

const MenuPanel: FC<MenuPanelProps> = ({ rootRef, triggerRef, closing }) => {
  // Меню стоит в DOM после шапки, поэтому без переноса фокуса Tab шёл бы через «Проекты»,
  // «Контакты» и тему. При открытии с кнопки-триггера переводим фокус на первый пункт.
  // closing в зависимостях: открытие во время анимации закрытия не монтирует панель заново
  useEffect(() => {
    if (closing) return;
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !active.closest('[data-catalog-trigger]')) return;
    triggerRef.current = active;
    rootRef.current?.querySelector<HTMLElement>('a[href]')?.focus();
  }, [closing, rootRef, triggerRef]);

  return (
    <nav
      className={styles.root}
      ref={rootRef}
      id={CATALOG_MENU_ID}
      aria-label="Каталог продукции"
      // Во время анимации закрытия ссылки уже не должны ловить клик и Tab.
      inert={closing}
      data-closing={closing}
    >
      <div className={styles.inner}>
        <ul className={styles.grid} aria-label="Разделы каталога">
          {AVAILABLE_CATEGORIES.map((cat) => (
            <li key={cat.id}>
              <Link
                className={styles.card}
                to={`/catalog/${cat.slug}`}
                onClick={catalogMenuStore.close}
              >
                {cat.coverImage && (
                  <span className={styles.preview}>
                    <Picture
                      className={styles.previewImg}
                      src={cat.coverImage}
                      alt=""
                      sizes={tokens.layout.menuThumb}
                    />
                  </span>
                )}
                <span className={styles.cardContent}>
                  <span className={styles.cardTitle}>{cat.title}</span>
                  <ItemCount slug={cat.slug} />
                </span>
                <ArrowRight className={styles.cardArrow} aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
        {UPCOMING_CATEGORIES.length > 0 && (
          <div className={styles.upcoming}>
            <p className={styles.upcomingLabel} id="catalog-upcoming-title">
              Скоро в каталоге
            </p>
            <ul className={styles.upcomingList} aria-labelledby="catalog-upcoming-title">
              {UPCOMING_CATEGORIES.map((cat) => (
                <li className={styles.cardSoon} key={cat.id} aria-disabled="true">
                  <span>{cat.title}</span>
                  {cat.sub && <span className={styles.soonSub}>{cat.sub}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </nav>
  );
};
